// Agregação de métricas do Dashboard. A maior parte é calculada no frontend
// a partir dos endpoints de listagem já existentes; "Conversas por dia" e os
// KPIs `conversasHoje`/`conversasPeriodo` usam antes uma agregação feita no
// servidor (`GET /chat-sessions/group/{id}/daily-counts`), sem teto de linhas
// — ver histórico: antes disto, esses três números vinham da mesma amostra
// capada a MAX_SESSIONS (200, ordenada por atividade mais recente) usada só
// para a tabela de sessões recentes, e por isso qualquer grupo com mais de
// 200 sessões ativas recentemente ficava com dias mais antigos a zero.
//
// `conversasPeriodo`/`reservasPeriodo`/`taxaResolucaoAutomatica`/
// `tempoMedioRespostaSeg` vêm todos acompanhados do valor do período anterior de
// igual duração (`*Anterior`), para o "Δ vs período anterior" pedido na secção 3 do
// plano Hotel Intelligence — calculado em DashboardView.jsx a partir destes pares.
//
// Limitações conhecidas (documentadas no plano):
// - `/chat-messages/group/{id}` não tem filtro de data; por isso buscamos
//   apenas a página mais recente (até MAX_MESSAGES, usando
//   `skip = total - limite` para obter o fim da lista ordenada
//   cronologicamente ASC). KPIs derivados de `messages` (taxa de resolução,
//   tempo médio de resposta, heatmap de horas, tópicos) ficam sujeitos a essa
//   amostra em grupos muito ativos — incluindo a comparação com o período
//   anterior, que precisa do dobro da janela dentro dessa mesma amostra capada.
// - Não há distinção "IA" vs "humano" nem sentimento no modelo de dados —
//   esses KPIs não aparecem aqui (ver plano, Fase 2).
// - "Principais intenções dos hóspedes" é uma heurística de palavras-chave sobre o
//   texto das mensagens do visitante, não uma classificação real de intenções (ver
//   INTENT_CATEGORIES/computeIntentions) — suficiente para o plano nesta fase (secção
//   5: "não implementar infraestrutura complexa de classificação se não for necessária").

const MAX_MESSAGES = 1000;
const MAX_SESSIONS = 200;

// Categorias de "Principais intenções dos hóspedes" (plano Hotel Intelligence, secção 5) —
// substitui o antigo agrupamento ad-hoc por check-in/check-out/spa/etc. pela lista sugerida no
// plano. Ordem = prioridade de classificação: cada mensagem conta para a PRIMEIRA categoria
// cujos termos encontrar (evita contar a mesma mensagem 2x); quem não encontrar nenhuma cai em
// "Outros" (ver computeIntentions) — não é ruído, é sinal honesto do que ainda não conseguimos
// classificar por keyword.
const INTENT_CATEGORIES = [
  {
    key: 'reservas',
    label: 'Reservas',
    terms: ['reserva', 'reservar', 'booking', 'disponibilidade'],
  },
  {
    key: 'restaurante',
    label: 'Restaurante / Bar',
    terms: ['restaurante', 'restaurant', 'bar', 'menu', 'jantar', 'almoço', 'pequeno-almoço', 'breakfast'],
  },
  {
    key: 'estacionamento',
    label: 'Estacionamento',
    terms: ['parking', 'estacionamento', 'garagem'],
  },
  {
    key: 'checkin_checkout',
    label: 'Check-in / Check-out',
    terms: ['check-in', 'checkin', 'check in', 'check-out', 'checkout', 'check out', 'late checkout'],
  },
  {
    key: 'transportes',
    label: 'Transportes',
    terms: ['transfer', 'transporte', 'táxi', 'taxi', 'aeroporto', 'shuttle'],
  },
  {
    key: 'eventos',
    label: 'Eventos',
    terms: ['evento', 'event', 'casamento', 'conferência', 'conferencia'],
  },
  {
    key: 'servicos',
    label: 'Serviços do Hotel',
    terms: ['lavandaria', 'concierge', 'receção', 'recepção', 'toalhas', 'limpeza'],
  },
  {
    key: 'spa_wellness',
    label: 'Spa / Wellness',
    terms: ['spa', 'massagem', 'wellness', 'ginásio', 'ginasio', 'gym'],
  },
  {
    key: 'quartos',
    label: 'Quartos',
    terms: ['quarto', 'room', 'cama', 'suite', 'piscina', 'pool', 'wifi', 'wi-fi'],
  },
  {
    key: 'ofertas',
    label: 'Ofertas / Promoções',
    terms: ['oferta', 'promoção', 'promocao', 'desconto', 'discount'],
  },
  {
    key: 'reclamacoes',
    label: 'Reclamações / Problemas',
    terms: ['reclamação', 'reclamacao', 'problema', 'complaint', 'insatisfeito', 'avariado', 'não funciona'],
  },
];
const OUTROS = { key: 'outros', label: 'Outros' };
const INTENT_SAMPLE_SIZE = 5;

function normalizeList(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.results)) return data.results;
  if (Array.isArray(data.items)) return data.items;
  return [];
}

function totalOf(data, fallbackLength) {
  if (data && typeof data.total === 'number') return data.total;
  return fallbackLength;
}

// "YYYY-MM-DD" a partir dos componentes LOCAIS da data — nunca `toISOString()`, que converte
// para UTC primeiro: em Portugal (UTC+1 no horário de verão), a meia-noite local de "hoje"
// corresponde às 23h de "ontem" em UTC, por isso `toISOString().slice(0,10)` devolvia sempre o
// dia UTC anterior. Isso desalinhava `conversasHoje`/"Conversas por dia" da agregação do
// servidor (`GET /chat-sessions/group/{id}/daily-counts`, que agrupa por dia UTC do timestamp
// real) — "Conversas hoje" nunca chegava a mostrar o dia certo, parecia nunca "resetar" à
// meia-noite. Os componentes locais coincidem com o dia UTC da atividade real fora da janela
// 00h-01h (Portugal só está 1h à frente), que é a única margem que continua sujeita a isto.
function dateKey(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function startOfDay(d) {
  const c = new Date(d);
  c.setHours(0, 0, 0, 0);
  return c;
}

async function fetchRecentMessages(axiosAuth, groupId, hotelId) {
  const hotelParams = hotelId ? { hotel_id: hotelId } : {};
  const probe = await axiosAuth.get(`/chat-messages/group/${groupId}`, {
    params: { skip: 0, limit: 1, ...hotelParams },
  });
  const total = totalOf(probe.data, 0);
  if (total === 0) return [];

  const limit = Math.min(MAX_MESSAGES, total);
  const skip = Math.max(0, total - limit);
  const res = await axiosAuth.get(`/chat-messages/group/${groupId}`, {
    params: { skip, limit, ...hotelParams },
  });
  return normalizeList(res.data);
}

// Distribuição de idiomas agregada no servidor, sobre TODO o histórico.
// Ao contrário do cálculo client-side (limitado às últimas MAX_MESSAGES),
// isto reflete correções em mensagens antigas. Devolve null em caso de erro
// para permitir fallback ao cálculo local.
async function fetchLanguageDistribution(axiosAuth, groupId, hotelId) {
  const res = await axiosAuth.get(
    `/chat-messages/group/${groupId}/language-distribution`,
    { params: hotelId ? { hotel_id: hotelId } : {} },
  );
  return Array.isArray(res.data) ? res.data : normalizeList(res.data);
}

function messageTimestamp(m) {
  const raw = m.timestamp || m.created_at || m.date;
  return raw ? new Date(raw) : null;
}

// Contagem diária de sessões, agregada no servidor sobre TODO o histórico do
// grupo (GET /chat-sessions/group/{id}/daily-counts) — ao contrário da
// amostra capada a MAX_SESSIONS usada no resto deste ficheiro, não perde dias
// mais antigos quando o grupo já tem mais de 200 sessões ativas recentemente.
async function fetchDailyConversationCounts(axiosAuth, groupId, days = 90, hotelId) {
  const res = await axiosAuth.get(
    `/chat-sessions/group/${groupId}/daily-counts`,
    { params: { days, ...(hotelId ? { hotel_id: hotelId } : {}) } },
  );
  return Array.isArray(res.data) ? res.data : [];
}

function buildConversationBuckets(dailyCounts, days) {
  const now = new Date();
  const byKey = new Map(dailyCounts.map((r) => [r.date, r.count]));
  const buckets = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = startOfDay(new Date(now));
    d.setDate(d.getDate() - i);
    const key = dateKey(d);
    buckets.push({
      date: key,
      label: d.toLocaleDateString('pt-PT', {
        day: '2-digit',
        month: '2-digit',
      }),
      count: byKey.get(key) || 0,
    });
  }
  return buckets;
}

function computeLanguageDistribution(messages) {
  const counts = new Map();
  messages
    .filter((m) => m.origin === 'visitor' || m.origin === undefined)
    .forEach((m) => {
      const lang = (m.language || '').slice(0, 2).toLowerCase() || 'outro';
      counts.set(lang, (counts.get(lang) || 0) + 1);
    });
  return [...counts.entries()]
    .map(([lang, count]) => ({ lang, count }))
    .sort((a, b) => b.count - a.count);
}

function computeHourHeatmap(messages) {
  // grade 7 (dia da semana) x 24 (hora), contagem de mensagens do visitante
  const grid = Array.from({ length: 7 }, () => Array(24).fill(0));
  messages
    .filter((m) => m.origin === 'visitor')
    .forEach((m) => {
      const ts = messageTimestamp(m);
      if (!ts) return;
      grid[ts.getDay()][ts.getHours()] += 1;
    });
  return grid;
}

const WEEKDAY_LABELS = [
  'Domingo',
  'Segunda-feira',
  'Terça-feira',
  'Quarta-feira',
  'Quinta-feira',
  'Sexta-feira',
  'Sábado',
];

// KPIs derivados do heatmap (plano Hotel Intelligence, secção 12): hora de maior utilização e
// dia da semana mais ativo. "% fora do horário habitual" fica de fora — não existe
// configuração de horário de atendimento por hotel (o plano pede explicitamente para não
// assumir um horário universal e não mostrar este KPI sem essa configuração).
function computeHeatmapPeaks(grid) {
  const hourTotals = Array(24).fill(0);
  const dayTotals = Array(7).fill(0);
  grid.forEach((row, day) => {
    row.forEach((count, hour) => {
      hourTotals[hour] += count;
      dayTotals[day] += count;
    });
  });
  const total = hourTotals.reduce((a, b) => a + b, 0);
  if (!total) return { peakHour: null, peakDayLabel: null };
  const peakHour = hourTotals.indexOf(Math.max(...hourTotals));
  const peakDay = dayTotals.indexOf(Math.max(...dayTotals));
  return { peakHour, peakDayLabel: WEEKDAY_LABELS[peakDay] };
}

// "Principais intenções dos hóspedes" (plano Hotel Intelligence, secção 5): cada mensagem do
// visitante conta para a PRIMEIRA categoria de INTENT_CATEGORIES cujos termos encontrar; sem
// nenhuma correspondência, cai em "Outros". `percentage` é sobre o total de mensagens
// classificadas no período (não sobre o total de conversas). `sample` guarda até
// INTENT_SAMPLE_SIZE mensagens (sessão + excerto) para o drill-down "ver conversas" no
// DashboardView — sem chamada extra ao backend, usa a mesma amostra já carregada.
function computeIntentions(messages) {
  const buckets = new Map(
    [...INTENT_CATEGORIES, OUTROS].map((c) => [c.key, { ...c, count: 0, sample: [] }]),
  );
  const visitorMessages = messages.filter((m) => m.origin === 'visitor' && m.content);

  visitorMessages.forEach((m) => {
    const text = m.content.toLowerCase();
    const match = INTENT_CATEGORIES.find((c) => c.terms.some((term) => text.includes(term)));
    const bucket = buckets.get(match ? match.key : OUTROS.key);
    bucket.count += 1;
    if (bucket.sample.length < INTENT_SAMPLE_SIZE) {
      bucket.sample.push({
        session_id: m.session_id,
        content: m.content.length > 160 ? `${m.content.slice(0, 160)}…` : m.content,
        timestamp: messageTimestamp(m),
      });
    }
  });

  const total = visitorMessages.length;
  return Array.from(buckets.values())
    .filter((b) => b.count > 0)
    .map((b) => ({
      key: b.key,
      label: b.label,
      count: b.count,
      percentage: total ? Math.round((b.count / total) * 1000) / 10 : 0,
      sample: b.sample,
    }))
    .sort((a, b) => b.count - a.count);
}

function computeHotelRanking(messages, hotelsById) {
  const byHotel = new Map();
  messages.forEach((m) => {
    const key = m.hotel_id ?? 'group';
    if (!byHotel.has(key)) {
      byHotel.set(key, {
        hotel_name:
          key === 'group'
            ? 'Chat geral'
            : hotelsById.get(m.hotel_id) || `Hotel #${m.hotel_id}`,
        conversas: 0,
        fallback: 0,
      });
    }
    const row = byHotel.get(key);
    if (m.origin === 'visitor') row.conversas += 1;
    if (m.origin === 'agent' && m.is_fallback) row.fallback += 1;
  });
  return [...byHotel.values()]
    .map((r) => ({
      ...r,
      taxa_fallback: r.conversas > 0 ? r.fallback / r.conversas : 0,
    }))
    .sort((a, b) => b.conversas - a.conversas);
}

function computeAvgResponseSeconds(messages) {
  const bySession = new Map();
  messages.forEach((m) => {
    if (!m.session_id) return;
    if (!bySession.has(m.session_id)) bySession.set(m.session_id, []);
    bySession.get(m.session_id).push(m);
  });

  const deltas = [];
  bySession.forEach((list) => {
    const sorted = [...list].sort((a, b) => {
      const ta = messageTimestamp(a)?.getTime() ?? 0;
      const tb = messageTimestamp(b)?.getTime() ?? 0;
      return ta - tb;
    });
    for (let i = 0; i < sorted.length - 1; i++) {
      const cur = sorted[i];
      const next = sorted[i + 1];
      if (cur.origin === 'visitor' && next.origin === 'agent') {
        const t1 = messageTimestamp(cur);
        const t2 = messageTimestamp(next);
        if (t1 && t2) {
          const diff = (t2.getTime() - t1.getTime()) / 1000;
          if (diff >= 0 && diff < 300) deltas.push(diff); // ignora outliers >5min (ex. sessão retomada horas depois)
        }
      }
    }
  });

  if (!deltas.length) return null;
  return deltas.reduce((a, b) => a + b, 0) / deltas.length;
}

function computeFallbackRate(messages) {
  const agentMessages = messages.filter((m) => m.origin === 'agent');
  if (!agentMessages.length) return null;
  const fallbacks = agentMessages.filter((m) => m.is_fallback).length;
  return fallbacks / agentMessages.length;
}

// Evolução diária de "Resolução automática" / "Sem resposta" (plano Hotel Intelligence,
// secção 7): mesmo agrupamento por dia de buildConversationBuckets, mas sobre as mensagens do
// AGENTE (não sessões) dentro da amostra de `messages` já capada — sujeito à mesma limitação
// documentada no topo do ficheiro (grupos muito ativos podem não ter histórico suficiente
// para preencher os 90 dias). `total: 0` nesse dia devolve percentagens `null` (sem dados),
// não 0%, para o gráfico não sugerir "100% sem resposta" onde não há amostra nenhuma.
function computeResolutionByDay(messages, days) {
  const now = new Date();
  const byDay = new Map();
  messages
    .filter((m) => m.origin === 'agent')
    .forEach((m) => {
      const ts = messageTimestamp(m);
      if (!ts) return;
      const key = dateKey(startOfDay(ts));
      const bucket = byDay.get(key) || { total: 0, fallback: 0 };
      bucket.total += 1;
      if (m.is_fallback) bucket.fallback += 1;
      byDay.set(key, bucket);
    });

  const series = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = startOfDay(new Date(now));
    d.setDate(d.getDate() - i);
    const key = dateKey(d);
    const bucket = byDay.get(key);
    const total = bucket?.total || 0;
    series.push({
      date: key,
      label: d.toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit' }),
      resolucaoPct: total ? Math.round(((total - bucket.fallback) / total) * 1000) / 10 : null,
      semRespostaPct: total ? Math.round((bucket.fallback / total) * 1000) / 10 : null,
      total,
    });
  }
  return series;
}

// início do período (N dias, incluindo hoje) — mesma janela usada por
// buildConversationBuckets, para o filtro de período ficar consistente entre
// o gráfico "Conversas por dia" e as métricas derivadas de `messages` abaixo.
function periodStart(days) {
  const d = startOfDay(new Date());
  d.setDate(d.getDate() - (days - 1));
  return d;
}

export async function loadDashboardData(axiosAuth, groupId, hotelId, periodDays = 30) {
  if (!groupId) return null;
  const hotelParams = hotelId ? { hotel_id: hotelId } : {};

  const [
    sessionsRes,
    hotelsRes,
    formEventsRes,
    unansweredRes,
    messages,
    languageDist,
    dailyCounts,
  ] = await Promise.all([
    axiosAuth.get('/chat-sessions', {
      params: { group_id: groupId, skip: 0, limit: MAX_SESSIONS, ...hotelParams },
    }),
    axiosAuth.get(`/hotels/group/${groupId}`).catch(() => ({ data: [] })),
    axiosAuth
      .get('/form-events/', {
        params: { group_id: groupId, skip: 0, limit: 200, ...hotelParams },
      })
      .catch(() => ({ data: { results: [] } })),
    axiosAuth
      .get(`/chat-messages/group/${groupId}/unaswered`, {
        params: { skip: 0, limit: 1, ...hotelParams },
      })
      .catch(() => ({ data: { total: 0 } })),
    fetchRecentMessages(axiosAuth, groupId, hotelId),
    fetchLanguageDistribution(axiosAuth, groupId, hotelId).catch(() => null),
    // 2x periodDays: cobre a janela atual e a anterior (mesmo comprimento), para a
    // comparação "Δ vs período anterior" (secção 3 do plano Hotel Intelligence) — sem
    // duplicar o pedido, já que a agregação diária é barata e o gráfico "Conversas por
    // dia" só mostra os últimos `periodDays` desta mesma série.
    fetchDailyConversationCounts(
      axiosAuth,
      groupId,
      Math.max(180, periodDays * 2),
      hotelId,
    ).catch(() => []),
  ]);

  const sessions = normalizeList(sessionsRes.data);
  const sessionsTotal = totalOf(sessionsRes.data, sessions.length);
  const hotels = Array.isArray(hotelsRes.data) ? hotelsRes.data : [];
  const hotelsById = new Map(hotels.map((h) => [h.id, h.name]));
  const formEvents = normalizeList(formEventsRes.data);
  const reservasTotais = totalOf(formEventsRes.data, formEvents.length);

  const now = new Date();
  const today = startOfDay(now);
  const todayKey = dateKey(today);
  // conversasHoje vem da agregação server-side (dailyCounts), não da amostra
  // `sessions` (capada a MAX_SESSIONS) — ver nota no topo do ficheiro sobre
  // grupos muito ativos ficarem incompletos nessa amostra.
  const conversasHoje =
    dailyCounts.find((r) => r.date === todayKey)?.count || 0;

  // Janela atual [cutoffCurrent, hoje] vs janela anterior de igual duração
  // [cutoffPrevious, cutoffCurrent) — base do "Δ vs período anterior" (secção 3 do
  // plano). `dailyCounts`/`formEvents`/`messages` já cobrem ambas as janelas.
  const cutoffCurrent = periodStart(periodDays);
  const cutoffCurrentKey = dateKey(cutoffCurrent);
  const cutoffPrevious = periodStart(periodDays * 2);
  const cutoffPreviousKey = dateKey(cutoffPrevious);

  const conversasPeriodo = dailyCounts
    .filter((r) => r.date >= cutoffCurrentKey)
    .reduce((sum, r) => sum + r.count, 0);
  const conversasPeriodoAnterior = dailyCounts
    .filter((r) => r.date >= cutoffPreviousKey && r.date < cutoffCurrentKey)
    .reduce((sum, r) => sum + r.count, 0);

  const reservasPeriodo = formEvents.filter(
    (f) => f.created_at && new Date(f.created_at) >= cutoffCurrent,
  ).length;
  const reservasPeriodoAnterior = formEvents.filter((f) => {
    if (!f.created_at) return false;
    const ts = new Date(f.created_at);
    return ts >= cutoffPrevious && ts < cutoffCurrent;
  }).length;

  // Preferir a agregação do servidor (todo o histórico); só cair no cálculo
  // local (amostra das últimas MAX_MESSAGES) se o endpoint falhar.
  const languages = Array.isArray(languageDist)
    ? languageDist
    : computeLanguageDistribution(messages);
  const topLanguage = languages[0]?.lang;

  // Filtro de período (secção 19 do plano Hotel Intelligence): as métricas abaixo derivam
  // da amostra `messages` (capada a MAX_MESSAGES, ver nota no topo do ficheiro) — sem isto,
  // cada uma respeitava uma janela diferente (e não documentada) da amostra capada.
  const periodMessages = messages.filter((m) => {
    const ts = messageTimestamp(m);
    return ts && ts >= cutoffCurrent;
  });
  const previousPeriodMessages = messages.filter((m) => {
    const ts = messageTimestamp(m);
    return ts && ts >= cutoffPrevious && ts < cutoffCurrent;
  });
  const hourHeatmap = computeHourHeatmap(periodMessages);

  const taxaResolucaoAutomaticaAnterior = (() => {
    const rate = computeFallbackRate(previousPeriodMessages);
    return rate === null ? null : 1 - rate;
  })();

  return {
    generatedAt: now,
    sampleSize: {
      sessions: sessions.length,
      sessionsTotal,
      messages: messages.length,
      periodMessages: periodMessages.length,
    },
    kpis: {
      conversasHoje,
      conversasPeriodo,
      conversasPeriodoAnterior,
      taxaResolucaoAutomatica: (() => {
        const rate = computeFallbackRate(periodMessages);
        return rate === null ? null : 1 - rate;
      })(),
      taxaResolucaoAutomaticaAnterior,
      tempoMedioRespostaSeg: computeAvgResponseSeconds(periodMessages),
      tempoMedioRespostaAnteriorSeg: computeAvgResponseSeconds(previousPeriodMessages),
      reservasPeriodo,
      reservasPeriodoAnterior,
      reservasTotais,
      idiomaMaisComum: topLanguage,
      perguntasSemResposta: totalOf(unansweredRes.data, 0),
    },
    conversationsByDay: {
      d7: buildConversationBuckets(dailyCounts, 7),
      d30: buildConversationBuckets(dailyCounts, 30),
      d90: buildConversationBuckets(dailyCounts, 90),
    },
    languages,
    hourHeatmap: hourHeatmap,
    heatmapPeaks: computeHeatmapPeaks(hourHeatmap),
    resolutionByDay: computeResolutionByDay(messages, periodDays),
    intentions: computeIntentions(periodMessages),
    // período anterior de igual duração — só para comparação de tendência (secção "Principais
    // intenções" e Insights IA, ver src/services/insights.js).
    previousIntentions: computeIntentions(previousPeriodMessages),
    hotelRanking: computeHotelRanking(periodMessages, hotelsById),
    recentSessions: sessions.slice(0, 6),
  };
}
